import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatPercentage, formatPhoneNumber } from "src/util/gFormatting";
import { tBrandReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const columns: tColumn<tBrandReport>[] = [
  {
    id: "acceptedDateInSeconds",
    header: "Approved Date",
    accessor: (originalRow) =>
      originalRow.acceptedDateInSeconds ? formatDate(originalRow.acceptedDateInSeconds) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "commissionFee",
    header: "Commission",
    accessor: (originalRow) => (originalRow.commissionFee ? formatPercentage(originalRow.commissionFee) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 100 },
  },
  {
    id: "marketplaceFee",
    header: "TMT Transaction fee",
    accessor: (originalRow) => (originalRow.marketplaceFee ? formatPercentage(originalRow.marketplaceFee) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 170 },
  },
  {
    id: "isExclusive",
    header: "Exclusive",
    accessor: (originalRow) => (originalRow.isExclusive ? "Exclusive" : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 100 },
  },
  {
    id: "contact.name",
    header: "Contact Name",
    accessor: (originalRow) => originalRow.contact?.name || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "contact.email",
    header: "Contact Email",
    accessor: (originalRow) => originalRow.contact?.email || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 250 },
  },
  {
    id: "contact.phone",
    header: "Phone Number",
    accessor: (originalRow) => (originalRow.contact?.phone ? formatPhoneNumber(originalRow.contact.phone) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "website",
    header: "Website",
    accessor: (originalRow) => originalRow.website,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "shippingAddressLine1",
    header: "Ship to Address Line 1",
    accessor: (originalRow) => originalRow.shippingAddressLine1,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "shippingAddressLine2",
    header: "Ship to Address Line 2",
    accessor: (originalRow) => originalRow.shippingAddressLine2 || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "shippingAddressCity",
    header: "Ship to City",
    accessor: (originalRow) => originalRow.shippingAddressCity,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "shippingAddressState",
    header: "Ship to State",
    accessor: (originalRow) => originalRow.shippingAddressState,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "shippingAddressZipCode",
    header: "Ship to Zip",
    accessor: (originalRow) => originalRow.shippingAddressZipCode,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "category",
    header: "Category",
    accessor: (originalRow) => originalRow.category || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "pricePoints",
    header: "What Retail price points are your products?",
    accessor: (originalRow) => originalRow.pricePoints || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "about",
    header: "About",
    accessor: (originalRow) => originalRow.about || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "isPublished",
    header: "Published",
    accessor: (originalRow) => (originalRow.isPublished ? "Yes" : "No"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "isSetupForSales",
    header: "Balance Connection",
    accessor: (originalRow) => (originalRow.isSetupForSales ? "Yes" : "No"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
];

const getBrandsReport = (start: Date, end: Date) =>
  api.get<tBrandReport[]>("reports/brands", getParams(start, end)).then((res) => res.data);

const BrandsTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.brandsExcelFormat);
  const { isLoading, data } = useFocusableQuery<tBrandReport[]>(
    useCallback(() => getBrandsReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.id}
      sortedBy="acceptedDateInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="brandsExcelFormat"
    />
  );
};

export default BrandsTable;
